// member-portal/src/components/PromoterSignup.js
// Features: password visibility, optional agency fields, terms checkbox + timestamp, rules link

import React, { useState } from 'react';
import { toast } from 'react-toastify';
import './PromoterSignup.css';

const PromoterSignup = ({ setView }) => {
  const [formData, setFormData] = useState({
    name: '',
    surname: '',
    email: '',
    password: '',
    confirmPassword: '',
    accountHolder: '',
    bankName: '',
    accountNumber: '',
    branchCode: '',
    companyName: '',
    website: '',
    telephone: '',
    agreedToTerms: false,
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    setError('');
  };

  const togglePasswordVisibility = () => setShowPassword(!showPassword);

  const validateForm = () => {
    if (!formData.email || !/\S+@\S+\.\S+/.test(formData.email)) {
      setError('Please enter a valid email address');
      return false;
    }
    if (!formData.password || formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return false;
    }
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    if (!formData.accountHolder || !formData.bankName || !formData.accountNumber || !formData.branchCode) {
      setError('Please fill in all bank details for payouts');
      return false;
    }
    if (!formData.agreedToTerms) {
      setError('You must accept the Promoter Program Rules & Terms to continue');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setError('');

    const termsAcceptedAt = new Date().toISOString();

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/promoters/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name.trim(),
          surname: formData.surname.trim(),
          email: formData.email.trim().toLowerCase(),
          password: formData.password,
          bankDetails: {
            accountHolder: formData.accountHolder.trim(),
            bankName: formData.bankName.trim(),
            accountNumber: formData.accountNumber.trim(),
            branchCode: formData.branchCode.trim(),
          },
          companyName: formData.companyName.trim() || null,
          website: formData.website.trim() || null,
          telephone: formData.telephone.trim() || null,
          termsAccepted: true,
          termsAcceptedAt,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create promoter account');
      }

      toast.success('Promoter account created! Check your email for verification instructions.', {
        autoClose: 5000,
      });

      setFormData({
        name: '', surname: '', email: '', password: '', confirmPassword: '',
        accountHolder: '', bankName: '', accountNumber: '', branchCode: '',
        companyName: '', website: '', telephone: '',
        agreedToTerms: false,
      });

      setTimeout(() => setView('login'), 2000);
    } catch (err) {
      setError(err.message);
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  const rulesUrl = 'https://www.scover.co.za/how-promoters-earn.html';

  return (
    <div className="promoter-signup-container">
      <div className="promoter-signup-card">
        <h1>Join as a Scover Promoter</h1>
        <p className="subtitle">
          Earn commissions by referring restaurants to Scover. It's free to join and simple to start.
        </p>

        <div className="rules-section">
          <a
            href={rulesUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="rules-button"
          >
            How Promoters Earn – Full Details & Rules
          </a>
          <p className="rules-hint">
            Please read the full commission structure, payout rules (min R500), and program terms before signing up.
          </p>
        </div>

        {error && <div className="error-message">{error}</div>}

        <form onSubmit={handleSubmit} className="promoter-form">
          <div className="form-row">
            <div className="form-group">
              <label>First Name *</label>
              <input
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                placeholder="e.g. Jane"
              />
            </div>
            <div className="form-group">
              <label>Last Name *</label>
              <input
                name="surname"
                value={formData.surname}
                onChange={handleChange}
                required
                placeholder="e.g. Doe"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Email Address *</label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="your@email.com"
            />
          </div>

          <div className="form-row">
            <div className="form-group password-group">
              <label>Password *</label>
              <div className="password-wrapper">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  placeholder="At least 8 characters"
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={togglePasswordVisibility}
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <div className="form-group password-group">
              <label>Confirm Password *</label>
              <input
                type={showPassword ? 'text' : 'password'}
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                placeholder="Re-enter password"
              />
            </div>
          </div>

          <h2>Company Details (optional — great for agencies)</h2>
          <div className="form-group">
            <label>Company Name</label>
            <input name="companyName" value={formData.companyName} onChange={handleChange} placeholder="e.g. Joe Marketing Agency" />
          </div>

          <div className="form-group">
            <label>Website</label>
            <input name="website" value={formData.website} onChange={handleChange} placeholder="https://youragency.com" />
          </div>

          <div className="form-group">
            <label>Telephone Number</label>
            <input name="telephone" value={formData.telephone} onChange={handleChange} placeholder="+27 82 123 4567" />
          </div>

          <h2>Bank Details (for commission payouts)</h2>
          <p className="bank-hint">We use this to pay your commissions monthly. All details are encrypted.</p>

          <div className="form-group">
            <label>Account Holder Name *</label>
            <input
              name="accountHolder"
              value={formData.accountHolder}
              onChange={handleChange}
              required
              placeholder="Name on bank account"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Bank Name *</label>
              <input
                name="bankName"
                value={formData.bankName}
                onChange={handleChange}
                required
                placeholder="e.g. FNB, Standard Bank"
              />
            </div>
            <div className="form-group">
              <label>Branch Code *</label>
              <input
                name="branchCode"
                value={formData.branchCode}
                onChange={handleChange}
                required
                placeholder="e.g. 250655"
              />
            </div>
          </div>

          <div className="form-group">
            <label>Account Number *</label>
            <input
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              required
              placeholder="Your bank account number"
            />
          </div>

          <div className="terms-section">
            <label className="terms-label">
              <input
                type="checkbox"
                name="agreedToTerms"
                checked={formData.agreedToTerms}
                onChange={handleChange}
                className="terms-checkbox"
              />
              <span>
                I have read and agree to the{' '}
                <a
                  href={rulesUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="terms-link"
                >
                  Promoter Program Rules & Terms
                </a>
              </span>
            </label>
          </div>

          <div className="form-actions">
            <button type="submit" disabled={loading} className="submit-btn">
              {loading ? 'Creating Account...' : 'Join as Promoter'}
            </button>
            <button type="button" className="cancel-btn" onClick={() => setView('landing')}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PromoterSignup;